import Heading from 'atoms/Heading';
import Text from 'atoms/Text';
import CountdownBar from 'atoms/CountdownBar';
import { Check } from 'react-feather';
import DialogBox from '@/molecules/DialogBox';
import MatchingChip from '@/molecules/MatchingChip';
import { isIrsButterflyFromStructure } from 'helper/irs.helper.service';
import { getMatchingCurveInterestDisplayName } from '@/helper/irsMatching.helper';
import useCommonStore from '@/store/commonStore';

const StatBox = ({ label, children, uppercase = true }) => (
    <div className="flex-1 border-r last:border-r-0 mr-2 last:mr-0 border-gray-500">
        <Text variant='caption' fontWeight='font-medium' className={`text-gray-200 ${uppercase ? 'uppercase' : ''}`}>{label}</Text>
        <div className="flex gap-1 items-center mt-1">
            {children}
        </div>
    </div>
);

const MatchSuccessfulDialog = ({ data, close, totalTime = 30, endTime }) => {
    const serverTime = useCommonStore((state) => state.serverTime);
    const remaining = data.rq ?? 0;

    const timeRemaining = (new Date(endTime).getTime() - serverTime) / 1000;
    const secondsLeft = Math.max(0, Math.round(timeRemaining));
    const isButterfly = isIrsButterflyFromStructure(data.s);
    const sizeLabel = isButterfly ? 'SIZE (Wings)' : 'SIZE';
    if (timeRemaining <= 0) return null;

    return (
        <DialogBox
            customPadding="p-8"
            bgColor='bg-gray-800 border border-gray-500'
            width="w-[450px]">
            <div>

                <div className="flex flex-col gap-1 pb-4 border-b border-gray-500 mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Check strokeWidth={3} size={22} className="text-success-300 border-2 border-success-300 rounded-full p-[3px]" />
                            <Heading type="h6" className="font-semibold text-success-300">MATCH SUCCESSFUL</Heading>
                        </div>
                        <MatchingChip theme={data.bt === 'IDB' ? 'idb' : 'agy'} />
                    </div>
                    <div className="flex items-center gap-2">
                        <Text variant='caption' className='text-gray-200'>{getMatchingCurveInterestDisplayName(data)}</Text>
                        <Text variant='caption' fontWeight='font-semibold' className='text-gray-200 border border-gray-500 px-1.5 py-0.5 rounded-sm'>{data.s}</Text>
                    </div>
                </div>

                <div className="w-full">

                    <div className="bg-gray-900 rounded-md p-3 flex flex-col items-center gap-2 justify-center mb-4">
                        <div className="flex items-end gap-1">
                            <Heading type="h1" className={`font-semibold ${data?.type === 'OFFER' ? 'text-error-300' : 'text-success-300'}`}>{data.q}</Heading>
                            <Text variant='caption' fontWeight='font-medium' className='text-gray-200'>K</Text>
                        </div>

                        <div className="border border-primary-100/25 bg-primary-110 flex items-center gap-1 px-3.5 h-6 rounded-md">
                            <div className="w-1.5 h-1.5 rounded bg-primary-100" />
                            <Text variant='caption' fontWeight='font-medium' className={'text-primary-100'}>{data.type}</Text>
                        </div>
                        <Text variant='bodySmall' className={'text-gray-300'}> Matched in this session.</Text>

                    </div>

                    <Text variant='body' className='text-gray-100 mb-3'>You have successfully matched in this session.</Text>

                    <div className="grid grid-cols-[1fr_1.5fr_1fr] items-center bg-gray-600/25 border border-gray-500 rounded-md px-3 py-2">
                        <StatBox label="PRICE">
                            <Text variant='bodySmall' fontWeight='font-semibold' className='text-success-300'>{data.p}</Text>
                        </StatBox>
                        <StatBox label={sizeLabel} uppercase={!isButterfly}>
                            <Text variant='bodySmall' fontWeight='font-semibold' className='text-gray-100'>{data.q}K</Text>
                            {remaining > 0 ? (
                                <p className="text-[10px] px-2 py-0 bg-warning-200/10 text-warning-200 border border-warning-200/25 rounded-sm font-semibold whitespace-nowrap">
                                    PARTIALLY FILLED
                                </p>
                            ) : (
                                <p className="text-[10px] px-2 py-0 bg-success-300/10 text-success-300 border border-success-300/25 rounded-sm font-semibold">
                                    FULLY FILLED
                                </p>
                            )}
                        </StatBox>
                        <StatBox label="REMAINING">
                            <Text variant='bodySmall' fontWeight='font-semibold' className={remaining > 0 ? 'text-warning-200' : 'text-gray-100'}>{remaining}K</Text>
                        </StatBox>
                    </div>
                </div>

                <button className="w-full mt-6 bg-success-300 text-gray-100 h-11 rounded-md text-base font-semibold active:scale-95"
                    onClick={close}>
                    OK
                </button>

                <div className="flex flex-col gap-2 mt-4">
                    <Text variant='bodySmall' fontWeight='font-medium' className='text-gray-200 text-center'>
                        Closing automatically in <span className="text-success-300">{secondsLeft}sec</span>
                    </Text>
                    <CountdownBar seconds={timeRemaining} total={totalTime} />
                </div>

            </div>
        </DialogBox>
    );
};

export default MatchSuccessfulDialog;
